import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { z } from "zod";
import { CheckIcon } from "@heroicons/react/20/solid";
import { IncidentClosedOutcome } from "@/gql/graphql";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { closeIncidentMutation } from "@/app/Incidents/GraphQL/closeIncidentMutation";
import { reopenIncidentMutation } from "@/app/Incidents/GraphQL/reopenIncidentMutation";

export function IncidentReasonDialog({
  incidentId,
  action,
}: {
  incidentId: string;
  action: "reject" | "reopen";
}) {
  const t = useTranslations("incident.decision");
  const { close } = useDialogState();
  const { mutateAsync: closeIncident } = useGqlMutation(closeIncidentMutation);
  const { mutateAsync: reopenIncident } = useGqlMutation(
    reopenIncidentMutation,
  );

  const schema = z.object({
    reason: z
      .string({ error: t("required") })
      .trim()
      .min(1, t("required")),
  });
  type ReasonFormValues = z.infer<typeof schema>;

  return (
    <BaseDialog
      title={t(`${action}.title`)}
      subtitle={t(`${action}.subtitle`)}
      size="xl"
    >
      <GenericForm<ReasonFormValues>
        initialValues={{ reason: "" }}
        validationSchema={toFormikValidationSchema(schema)}
        onSubmit={async (values) => {
          const reason = values.reason.trim();

          if (action === "reject") {
            await closeIncident({
              id: incidentId,
              outcome: IncidentClosedOutcome.Rejected,
              rejection_reason: reason,
            });
          } else {
            await reopenIncident({ id: incidentId, reason });
          }

          toast.success(t(`${action}.done`));
          await close();
        }}
      >
        <TextAreaInput
          name="reason"
          label={t("reason")}
          className="col-span-full"
          textAreaProps={{ rows: 3, maxLength: 5000 }}
        />
        <DialogSubmitButton text={t(`${action}.confirm`)} Icon={CheckIcon} />
      </GenericForm>
    </BaseDialog>
  );
}
